import React, {useRef, useState} from "react";
import {Formik} from "formik";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import SendIcon from "@mui/icons-material/Send";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ReCAPTCHA from "react-google-recaptcha";
import {useFirebaseState} from "../firebase/firebaseContext";
import {httpsCallable} from "firebase/functions";

const PRICE_PER_BOOK = 599;

const orderSchema = Yup.object().shape({
    numberOfBooks: Yup.number()
        .min(1, "Minst 1 bok må bestilles")
        .required("Vennligst oppgi antall bøker"),
    name: Yup.string().trim().required("Navn er påkrevd"),
    email: Yup.string().trim().email("Ugyldig e-postadresse").required("E-postadresse er påkrevd"),
    phone: Yup.string().trim().required("Telefonnummer er påkrevd"),
    addressName: Yup.string().trim().required("Mottaker/virksomhet er påkrevd"),
    addressStreet: Yup.string().trim().required("Gateadresse er påkrevd"),
    addressAreaCode: Yup.string().trim().required("Postnummer er påkrevd"),
    addressPlace: Yup.string().trim().required("Poststed er påkrevd"),
    contactPerson: Yup.string().trim().required("Kontaktperson er påkrevd"),
    contactPhone: Yup.string().trim().required("Telefonnummer for kontaktperson er påkrevd"),
    invoiceName: Yup.string().trim().required("Fakturanavn/virksomhet er påkrevd"),
    invoiceOrgNumber: Yup.string().trim(),
    invoiceReference: Yup.string().trim(),
    invoiceAddress: Yup.string().trim().required("Fakturaadresse er påkrevd"),
    invoiceAreaCode: Yup.string().trim().required("Faktura postnummer er påkrevd"),
    invoicePlace: Yup.string().trim().required("Faktura poststed er påkrevd"),
});

export const BookOrderForm = ({onSuccess, pricePerBook = PRICE_PER_BOOK}) => {
    const {functions} = useFirebaseState();
    const recaptchaRef = useRef();
    const activePrice = Number(pricePerBook) > 0 ? Number(pricePerBook) : PRICE_PER_BOOK;

    const [submitting, setSubmitting] = useState(false);
    const [orderError, setOrderError] = useState(null);
    const [sameAddress, setSameAddress] = useState(false);

    const handleSubmit = async (values) => {
        setSubmitting(true);
        setOrderError(null);

        try {
            let token = "development-token";
            if (recaptchaRef.current && typeof recaptchaRef.current.executeAsync === "function") {
                token = await recaptchaRef.current.executeAsync();
            }

            if (functions) {
                const submitOrder = httpsCallable(functions, "submitBookOrder");
                await submitOrder({token, order: values});
            }

            if (typeof onSuccess === "function") {
                onSuccess({
                    numberOfBooks: values.numberOfBooks,
                    totalAmount: values.numberOfBooks * activePrice,
                    email: values.email,
                    name: values.name,
                    delivery: {
                        name: values.addressName,
                        street: values.addressStreet,
                        areaCode: values.addressAreaCode,
                        place: values.addressPlace,
                    },
                });
            }
        } catch (err) {
            console.error("Feil ved bestilling:", err);
            setOrderError(err.message || "Kunne ikke fullføre bestillingen. Vennligst sjekk feltene og prøv igjen.");
        } finally {
            setSubmitting(false);
            if (recaptchaRef.current && typeof recaptchaRef.current.reset === "function") {
                recaptchaRef.current.reset();
            }
        }
    };

    return (
        <Box sx={{width: "100%", maxWidth: "100%", minWidth: 0}}>
            {orderError && (
                <Alert severity="error" sx={{mb: 3, borderRadius: 2}} onClose={() => setOrderError(null)}>
                    {orderError}
                </Alert>
            )}

            <Formik
                initialValues={{
                    numberOfBooks: 1,
                    name: "",
                    email: "",
                    phone: "",
                    addressName: "",
                    addressStreet: "",
                    addressAreaCode: "",
                    addressPlace: "",
                    contactPerson: "",
                    contactPhone: "",
                    invoiceName: "",
                    invoiceOrgNumber: "",
                    invoiceReference: "",
                    invoiceAddress: "",
                    invoiceAreaCode: "",
                    invoicePlace: "",
                }}
                validationSchema={orderSchema}
                onSubmit={handleSubmit}
            >
                {({values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue}) => {
                    const totalSum = (Number(values.numberOfBooks) || 0) * activePrice;

                    const handleSameAddressChange = (e) => {
                        const checked = e.target.checked;
                        setSameAddress(checked);
                        if (checked) {
                            setFieldValue("invoiceName", values.addressName);
                            setFieldValue("invoiceAddress", values.addressStreet);
                            setFieldValue("invoiceAreaCode", values.addressAreaCode);
                            setFieldValue("invoicePlace", values.addressPlace);
                        }
                    };

                    return (
                        <form onSubmit={handleSubmit} noValidate>
                            {/* Section 1: Antall og kontakt */}
                            <Box
                                sx={{
                                    p: {xs: 2.5, sm: 3.5},
                                    borderRadius: 3,
                                    border: "1px solid",
                                    borderColor: "rgba(40, 82, 56, 0.15)",
                                    bgcolor: "background.paper",
                                    mb: 3,
                                }}
                            >
                                <Box sx={{display: "flex", alignItems: "center", gap: 1.25, mb: 2}}>
                                    <ShoppingBagIcon sx={{color: "primary.main"}} />
                                    <Typography variant="h6" sx={{fontWeight: 700}}>
                                        1. Antall bøker og bestiller
                                    </Typography>
                                </Box>

                                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: 2, mb: 2}}>
                                    <TextField
                                        fullWidth
                                        id="numberOfBooks"
                                        name="numberOfBooks"
                                        label="Antall bøker"
                                        type="number"
                                        inputProps={{min: 1}}
                                        value={values.numberOfBooks}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.numberOfBooks && Boolean(errors.numberOfBooks)}
                                        helperText={touched.numberOfBooks && errors.numberOfBooks}
                                    />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            p: 1.5,
                                            bgcolor: "rgba(40, 82, 56, 0.05)",
                                            borderRadius: 2,
                                            border: "1px solid rgba(40, 82, 56, 0.12)",
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            Totalsum inkl. frakt:
                                        </Typography>
                                        <Typography variant="h6" sx={{fontWeight: 800, color: "primary.main"}}>
                                            {totalSum} kr
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(3, 1fr)"}, gap: 2}}>
                                    <TextField
                                        fullWidth
                                        id="name"
                                        name="name"
                                        label="Ditt navn"
                                        autoComplete="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name && errors.name}
                                    />
                                    <TextField
                                        fullWidth
                                        id="email"
                                        name="email"
                                        label="E-postadresse"
                                        type="email"
                                        autoComplete="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.email && Boolean(errors.email)}
                                        helperText={touched.email && errors.email}
                                    />
                                    <TextField
                                        fullWidth
                                        id="phone"
                                        name="phone"
                                        label="Telefonnummer"
                                        type="tel"
                                        autoComplete="tel"
                                        value={values.phone}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.phone && Boolean(errors.phone)}
                                        helperText={touched.phone && errors.phone}
                                    />
                                </Box>
                            </Box>

                            {/* Section 2: Leveringsadresse */}
                            <Box
                                sx={{
                                    p: {xs: 2.5, sm: 3.5},
                                    borderRadius: 3,
                                    border: "1px solid",
                                    borderColor: "rgba(40, 82, 56, 0.15)",
                                    bgcolor: "background.paper",
                                    mb: 3,
                                }}
                            >
                                <Box sx={{display: "flex", alignItems: "center", gap: 1.25, mb: 1}}>
                                    <LocalShippingIcon sx={{color: "primary.main"}} />
                                    <Typography variant="h6" sx={{fontWeight: 700}}>
                                        2. Leveringsadresse
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{mb: 2.5}}>
                                    Oppgi gateadresse/besøksadresse (ikke postboks) for rask leveranse direkte på døren.
                                </Typography>

                                <Box sx={{display: "grid", gridTemplateColumns: "1fr", gap: 2, mb: 2}}>
                                    <TextField
                                        fullWidth
                                        id="addressName"
                                        name="addressName"
                                        label="Mottaker / Virksomhet"
                                        value={values.addressName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.addressName && Boolean(errors.addressName)}
                                        helperText={touched.addressName && errors.addressName}
                                    />
                                    <TextField
                                        fullWidth
                                        id="addressStreet"
                                        name="addressStreet"
                                        label="Gate- og besøksadresse"
                                        value={values.addressStreet}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.addressStreet && Boolean(errors.addressStreet)}
                                        helperText={touched.addressStreet && errors.addressStreet}
                                    />
                                </Box>

                                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 2fr"}, gap: 2, mb: 2}}>
                                    <TextField
                                        fullWidth
                                        id="addressAreaCode"
                                        name="addressAreaCode"
                                        label="Postnummer"
                                        value={values.addressAreaCode}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.addressAreaCode && Boolean(errors.addressAreaCode)}
                                        helperText={touched.addressAreaCode && errors.addressAreaCode}
                                    />
                                    <TextField
                                        fullWidth
                                        id="addressPlace"
                                        name="addressPlace"
                                        label="Poststed"
                                        value={values.addressPlace}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.addressPlace && Boolean(errors.addressPlace)}
                                        helperText={touched.addressPlace && errors.addressPlace}
                                    />
                                </Box>

                                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: 2}}>
                                    <TextField
                                        fullWidth
                                        id="contactPerson"
                                        name="contactPerson"
                                        label="Kontaktperson for levering"
                                        value={values.contactPerson}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.contactPerson && Boolean(errors.contactPerson)}
                                        helperText={touched.contactPerson && errors.contactPerson}
                                    />
                                    <TextField
                                        fullWidth
                                        id="contactPhone"
                                        name="contactPhone"
                                        label="Telefon kontaktperson"
                                        type="tel"
                                        autoComplete="tel"
                                        value={values.contactPhone}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.contactPhone && Boolean(errors.contactPhone)}
                                        helperText={touched.contactPhone && errors.contactPhone}
                                    />
                                </Box>
                            </Box>

                            {/* Section 3: Fakturaopplysninger */}
                            <Box
                                sx={{
                                    p: {xs: 2.5, sm: 3.5},
                                    borderRadius: 3,
                                    border: "1px solid",
                                    borderColor: "rgba(40, 82, 56, 0.15)",
                                    bgcolor: "background.paper",
                                    mb: 3,
                                }}
                            >
                                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1}}>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                                        <ReceiptIcon sx={{color: "primary.main"}} />
                                        <Typography variant="h6" sx={{fontWeight: 700}}>
                                            3. Fakturaopplysninger
                                        </Typography>
                                    </Box>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={sameAddress}
                                                onChange={handleSameAddressChange}
                                                color="primary"
                                            />
                                        }
                                        label="Fakturaadresse er samme som leveringsadresse"
                                    />
                                </Box>

                                <Box sx={{display: "grid", gridTemplateColumns: "1fr", gap: 2, mb: 2}}>
                                    <TextField
                                        fullWidth
                                        id="invoiceName"
                                        name="invoiceName"
                                        label="Fakturanavn / Virksomhet"
                                        value={values.invoiceName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.invoiceName && Boolean(errors.invoiceName)}
                                        helperText={touched.invoiceName && errors.invoiceName}
                                    />
                                </Box>

                                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: 2, mb: 2}}>
                                    <TextField
                                        fullWidth
                                        id="invoiceOrgNumber"
                                        name="invoiceOrgNumber"
                                        label="Organisasjonsnummer (valgfritt for privat)"
                                        value={values.invoiceOrgNumber}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                    <TextField
                                        fullWidth
                                        id="invoiceReference"
                                        name="invoiceReference"
                                        label="Ressursnr / Bestillerreferanse (valgfritt)"
                                        value={values.invoiceReference}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                </Box>

                                <Box sx={{display: "grid", gridTemplateColumns: "1fr", gap: 2, mb: 2}}>
                                    <TextField
                                        fullWidth
                                        id="invoiceAddress"
                                        name="invoiceAddress"
                                        label="Fakturaadresse"
                                        value={values.invoiceAddress}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.invoiceAddress && Boolean(errors.invoiceAddress)}
                                        helperText={touched.invoiceAddress && errors.invoiceAddress}
                                    />
                                </Box>

                                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 2fr"}, gap: 2}}>
                                    <TextField
                                        fullWidth
                                        id="invoiceAreaCode"
                                        name="invoiceAreaCode"
                                        label="Faktura postnummer"
                                        value={values.invoiceAreaCode}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.invoiceAreaCode && Boolean(errors.invoiceAreaCode)}
                                        helperText={touched.invoiceAreaCode && errors.invoiceAreaCode}
                                    />
                                    <TextField
                                        fullWidth
                                        id="invoicePlace"
                                        name="invoicePlace"
                                        label="Faktura poststed"
                                        value={values.invoicePlace}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.invoicePlace && Boolean(errors.invoicePlace)}
                                        helperText={touched.invoicePlace && errors.invoicePlace}
                                    />
                                </Box>
                            </Box>

                            {/* Submit & ReCAPTCHA */}
                            <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: 2}}>
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    size="invisible"
                                    sitekey="6Lc7c9kUAAAAAMF3Q6vH3uL0q5bW2E0Q_example"
                                />

                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    disabled={submitting}
                                    endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: {xs: "0.95rem", sm: "1.05rem"},
                                        py: 1.5,
                                        px: {xs: 2.5, sm: 5},
                                        maxWidth: "100%",
                                        borderRadius: 3,
                                        boxShadow: "0 4px 16px rgba(40, 82, 56, 0.25)",
                                    }}
                                >
                                    {submitting ? "Sender bestilling..." : `Send inn bestilling (${totalSum} kr)`}
                                </Button>
                            </Box>
                        </form>
                    );
                }}
            </Formik>
        </Box>
    );
};